import countries from 'world-countries';

const UI_TO_WORLD_COUNTRIES: Record<string, string> = {
  en: 'eng',
  fi: 'fin',
  sv: 'swe',
  de: 'deu',
  et: 'est',
  fr: 'fra',
  ar: 'ara',
  zh: 'zho',
};

type CountryLike = {
  cca2: string;
  name: { common: string };
  translations?: Record<string, { common?: string }>;
};

export function worldCountriesLangKey(uiLanguage: string): string | null {
  const iso2 = uiLanguage.trim().toLowerCase().split(/[-_]/)[0];
  if (!iso2 || iso2 === 'en') return null;
  return UI_TO_WORLD_COUNTRIES[iso2] ?? (iso2.length === 3 ? iso2 : null);
}

export function getLocalizedCountryName(
  country: CountryLike,
  uiLanguage: string
): string {
  const key = worldCountriesLangKey(uiLanguage);
  const translated = key ? country.translations?.[key]?.common : undefined;
  return translated || country.name.common || country.cca2;
}

export function localizedCountryOptions(uiLanguage: string): {
  code: string;
  name: string;
}[] {
  const locale = uiLanguage.trim().toLowerCase().split(/[-_]/)[0] || 'en';
  return countries
    .map((c) => ({
      code: c.cca2,
      name: getLocalizedCountryName(c, uiLanguage),
    }))
    .sort((a, b) => a.name.localeCompare(b.name, locale));
}

export function englishCountryName(iso2: string): string {
  const country = countries.find((c) => c.cca2 === iso2.toUpperCase());
  return country?.name.common || iso2;
}

/** Resolve a stored KYC country (ISO or English name) to ISO alpha-2. */
export function resolveCountryToIso2(value?: string | null): string | null {
  if (!value?.trim()) return null;
  const trimmed = value.trim();
  if (/^[A-Za-z]{2}$/.test(trimmed)) {
    return trimmed.toUpperCase();
  }
  const lowered = trimmed.toLowerCase();
  const match = countries.find(
    (c) =>
      c.name.common.toLowerCase() === lowered ||
      c.name.official.toLowerCase() === lowered
  );
  return match?.cca2 ?? null;
}

/** Same country identity displays as the same English name whether stored as FI or Finland. */
export function displayKycCountry(value?: string | null): string {
  if (!value?.trim()) return '';
  const iso = resolveCountryToIso2(value);
  return iso ? englishCountryName(iso) : value;
}
